import React, { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import API from '../api/api';

const NOT_UTF8 = 'FILE_NOT_UTF8';

// what an import row is called: its source text, or item and field for advanced files
const rowLabel = (entry) => entry.source !== null && entry.source !== undefined
  ? entry.source
  : `${entry.item} · ${entry.field}`;

// the examples of all warnings as one list, a row with several problems once
const affectedRows = (warnings) => {
  const rows = new Map();
  warnings.forEach(warning => warning.examples.forEach(entry => {
    const key = `${rowLabel(entry)}\u0000${entry.target}`;
    if(!rows.has(key)) {
      rows.set(key, { ...entry, problems: [] });
    }
    rows.get(key).problems.push(warning.type);
  }));
  return [...rows.values()];
};

const cellSx = { wordBreak: 'break-word', verticalAlign: 'top' };

const FileName = ({ name, t }) => name ? (
  <Typography variant="body2" sx={{ mb: 2, wordBreak: 'break-all' }}>
    {t('importFeature.review.fileLabel')}: <strong>{name}</strong>
  </Typography>
) : null;

const AffectedTable = ({ rows, t }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell sx={{ width: '35%' }}>{t('importFeature.review.sourceColumn')}</TableCell>
        <TableCell>{t('importFeature.review.targetColumn')}</TableCell>
        <TableCell sx={{ width: '22%' }}>{t('importFeature.review.problemColumn')}</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map((row, index) => (
        <TableRow key={index}>
          <TableCell sx={cellSx}>{rowLabel(row)}</TableCell>
          <TableCell sx={cellSx}>{row.target}</TableCell>
          <TableCell sx={cellSx}>
            {row.problems.map(type => t(`importFeature.review.problems.${type}`)).join(', ')}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

// shown between upload and import when a file looks damaged, so the text can be checked before it lands in Shopify
const ImportReviewDialog = () => {
  const { t } = useTranslation();
  const [check, setCheck] = useState(null);
  const resolveRef = useRef(null);

  useEffect(() => {
    API.reviewImportFile = (importCheck) => new Promise(resolve => {
      resolveRef.current = resolve;
      setCheck(importCheck);
    });
    return () => {
      API.reviewImportFile = null;
    };
  }, []);

  const close = (confirmed) => {
    setCheck(null);
    if(resolveRef.current) {
      resolveRef.current(confirmed);
      resolveRef.current = null;
    }
  };

  if(!check) {
    return null;
  }

  if(check.error === NOT_UTF8) {
    return (
      <Dialog open onClose={() => close(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('importFeature.review.notUtf8Title')}</DialogTitle>
        <DialogContent>
          <FileName name={check.fileName} t={t} />
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('importFeature.review.notUtf8Text')}
          </Alert>
          <DialogContentText>{t('importFeature.review.notUtf8Fix')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => close(false)} color="primary" variant="contained">{t('system.close')}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const rows = affectedRows(check.warnings || []);

  return (
    <Dialog open onClose={() => close(false)} maxWidth="md" fullWidth>
      <DialogTitle>{t('importFeature.review.warningTitle')}</DialogTitle>
      <DialogContent>
        <FileName name={check.fileName} t={t} />
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('importFeature.review.warningText', { count: check.affected, total: check.count })}
          <strong style={{ display: 'block' }}>{t('importFeature.review.warningConsequence')}</strong>
        </Alert>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('importFeature.review.examplesTitle')}</Typography>
        <AffectedTable rows={rows} t={t} />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => close(false)} color="primary">{t('system.cancel')}</Button>
        <Button onClick={() => close(true)} color="primary" variant="contained">{t('importFeature.review.importAnyway')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportReviewDialog;
